/**
 * The FX argument readers of LSEG's FX functions (RDP.Fx*), ported from
 * Workspace's Excel add-in (Refinitiv for Office 26.30.2): the FX cross code,
 * spot and swap-point overrides as text (ForexUtils), spot overrides from a
 * cell range (SpotCellRangeProcessor) and zero-coupon curves from a cell
 * range (ZcCurveCellRangeProcessor, with its two helpers).
 *
 * The method names are LSEG's, so the logic can be compared with the source;
 * each carries a `// LSEG: <module>.<method>` pointer. Behaviour the source
 * does not settle is marked [A].
 */
import { AFCellRangeProcessor, ForexOverrides } from "./baseProcessor";
import { ADDITIONAL_TENOR_TYPES, FX_SOURCE_VALUES } from "./data.generated";
import { AdFinUtils, CellRange, isFxSource, isNumber, withoutEmptyValues } from "./utils";

/** A spot override as LSEG sends it (`spotCcy1` / `spotCcy2`). */
export type SpotOverride = { source?: string; bid?: number; ask?: number };

/** A zero-coupon curve as LSEG sends it (`zcCurve`). */
export type ZcCurve = { code?: unknown; curvePoints?: Record<string, unknown>[] };

const SOURCE_LABEL = "source";
const SPOT_LABEL = "spot";
const ADDITIONAL_TENOR_TYPES_LABEL = "additionaltenortypes";
const BID_ASK_SEPARATORS = /[,; |]/;

// LSEG: sources_50612.sources (its Composite entry)
const COMPOSITE = FX_SOURCE_VALUES.get("Composite");

/** The text being read by extractForexData. */
type ReadState = {
  char: string;
  currentValue: string;
  insideBrackets: boolean;
  bracketType: string;
  openingBracketCount: number;
};

/** What extractForexData found. */
type ForexData = {
  source: string;
  additionalTenorTypes: (string | undefined)[];
  bid: number;
  ask: number;
  overrides: Record<string, unknown>[];
};

/** A bracketed override: "label(override)". */
type BracketedOverride = { override: string; label: string; separators: RegExp };

/**
 * [A] A cell or argument LSEG calls a text method on (trim, toLowerCase) is
 * read as its text: LSEG fails with a TypeError on a number there.
 */
function text(value: unknown): string {
  return `${value}`;
}

/** The FX cross code, and spot and swap-point overrides typed as text. */
// LSEG: ForexUtils_75582.ForexUtils
export class ForexUtils extends AdFinUtils {
  private readonly aFCellRangeProcessor = new AFCellRangeProcessor();
  private readonly spotCellRangeProcessor = new SpotCellRangeProcessor();

  /**
   * "EURUSD", "eur usd", "FX cross code:EURUSD", or a range of one cell or a
   * label cell then the code. The code, upper-cased without spaces, when it
   * reads as known currencies; else the text as typed (the API answers).
   */
  // LSEG: ForexUtils_75582.getFxCrossCode
  getFxCrossCode(value: unknown): string {
    const fieldName = "FX cross code";
    this.validateNotEmpty(value, fieldName);
    if (Array.isArray(value)) {
      const tooBig = value[0].length > 2 || value.length > 2;
      this.validate(!tooBig, fieldName, "not in the right format");
      value = this.aFCellRangeProcessor.processCellRangeSingleArgument(value, 0);
    }
    const asTyped = text(value).trim();
    let code = text(value).replace(/\s+/g, "").toUpperCase();
    if (code.toLowerCase().includes(":")) {
      if (code.split(":")[0]?.trim().toLowerCase() !== "fxcrosscode") this.validate(false, fieldName, "provided wrong label");
      code = code.split(":")[1];
    }
    const out = this.validateAndReturnCurrencyCode(code, asTyped);
    this.validate(out !== null, fieldName, "bad syntax, expect 'Ccy1 Ccy2' or 'Ccy1 Ccy2 Ccy3'");
    return out;
  }

  /** "D3", "source:D3", "spot(1.1;1.2)", "D3 spot(bid:1.1)", or a range (SpotCellRangeProcessor). */
  // LSEG: ForexUtils_75582.getSpotCcy
  getSpotCcy(value: unknown, fieldName: string): SpotOverride | undefined {
    if (!value) return undefined;
    // LSEG: AFCellRangeProcessor_29882.processCellRangeSpotCcy (a pass-through)
    if (Array.isArray(value)) return this.spotCellRangeProcessor.processSpotCcy(value, fieldName);
    const data = this.extractForexData(text(value).trim(), true, fieldName);
    return withoutEmptyValues({
      source: data.source,
      ...(data.bid ? { bid: +data.bid } : {}),
      ...(data.ask ? { ask: +data.ask } : {}),
    });
  }

  /**
   * "1M(10;12) 3M(bid:20)", "additionalTenorTypes(Odd;Long)", "Odd", "EBS",
   * or a range (AFCellRangeProcessor.processCellRangeSwapPointsCcy).
   */
  // LSEG: ForexUtils_75582.getForexOverrides
  getForexOverrides(value: unknown, fieldName: string): ForexOverrides | undefined {
    if (!value) return undefined;
    if (Array.isArray(value)) return this.aFCellRangeProcessor.processCellRangeSwapPointsCcy(value, fieldName);
    const data = this.extractForexData(text(value).trim(), false, fieldName);
    return withoutEmptyValues({
      additionalTenorTypes: data.additionalTenorTypes,
      source: data.source,
      overrides: data.overrides,
    });
  }

  /** Reads space-separated parameters and bracketed overrides. */
  // LSEG: ForexUtils_75582.extractForexData
  private extractForexData(input: string, isSpot: boolean, fieldName: string): ForexData {
    const state: ReadState = { char: "", currentValue: "", insideBrackets: false, bracketType: "", openingBracketCount: 0 };
    const data: ForexData = { source: "", additionalTenorTypes: [], bid: 0, ask: 0, overrides: [] };
    for (const c of input) {
      state.char = c;
      if (c === "[" || c === "{" || c === "(") this.confirmOpeningBracket(state);
      else if (c === "]" || c === "}" || c === ")") this.confirmClosingBracket(state, data, isSpot, fieldName);
      else if (c === " ") this.confirmValuesSeparator(state, data, fieldName);
      else state.currentValue += c;
    }
    if (state.openingBracketCount !== 0) this.validate(false, fieldName, "second opening bracket found within the overrides");
    if (state.currentValue.trim() !== "") this.validateForexSingleParameter(state.currentValue, data, fieldName);
    return data;
  }

  /** "spot(1.1;1.2)" sets bid and ask; "1M(10;12)" adds an override; "additionalTenorTypes(...)" adds types. */
  // LSEG: ForexUtils_75582.validateForexOverride
  private validateForexOverride(state: ReadState, data: ForexData, isSpot: boolean, fieldName: string): void {
    const value = state.currentValue;
    if (!value) return;
    const parts = value.split(state.bracketType);
    const label = parts[0].endsWith(":") ? parts[0].slice(0, -1).trim().toLowerCase() : parts[0].trim().toLowerCase();
    const bracketed: BracketedOverride = {
      override: parts[1].slice(0, -1).replace(/\s+/g, " "),
      label,
      separators: BID_ASK_SEPARATORS,
    };
    if (!bracketed.override) this.validate(false, fieldName, "override is missing");
    if (isSpot) {
      if (bracketed.label && bracketed.label !== SPOT_LABEL) this.validate(false, fieldName, "provided wrong spot label for overrides");
      const { bid, ask } = this.getBitAskValues(bracketed.override);
      data.bid = Number(bid);
      data.ask = Number(ask);
    } else {
      this.validateValueInBrackets(bracketed, data, fieldName);
    }
  }

  /** "source:D3" or "additionalTenorTypes:Odd"; another label is ignored. `param` is lower-case. */
  // LSEG: ForexUtils_75582.processParamWithLabel
  private processParamWithLabel(param: string, data: ForexData, fieldName: string): void {
    const [label, value] = param.split(":");
    if (label === SOURCE_LABEL) {
      if (isFxSource(value.toUpperCase())) {
        if (data.source) this.validate(false, fieldName, "only one source can be provided");
        data.source = value.toUpperCase();
        return;
      }
      this.validate(false, fieldName, "provided wrong source");
    } else if (label === ADDITIONAL_TENOR_TYPES_LABEL) {
      if (!ADDITIONAL_TENOR_TYPES.has(value)) this.validate(false, fieldName, "provided wrong additionalTenorType");
      data.additionalTenorTypes.push(ADDITIONAL_TENOR_TYPES.get(value.trim().toLowerCase()));
    }
  }

  /** A source ("D3"), an additional tenor type ("Odd") or a labelled one. */
  // LSEG: ForexUtils_75582.validateForexSingleParameter
  private validateForexSingleParameter(param: string, data: ForexData, fieldName: string): void {
    if (!param) return;
    const lower = param.trim().toLowerCase();
    if (lower.includes(":")) {
      this.processParamWithLabel(lower, data, fieldName);
      return;
    }
    if (isFxSource(lower.toUpperCase())) {
      if (data.source) this.validate(false, fieldName, "only one source can be provided");
      data.source = lower.toUpperCase();
      return;
    }
    if (ADDITIONAL_TENOR_TYPES.has(lower)) {
      data.additionalTenorTypes.push(ADDITIONAL_TENOR_TYPES.get(lower));
      return;
    }
    this.validate(false, fieldName, "provided wrong parameter");
  }

  // LSEG: ForexUtils_75582.getOpeningBracket
  private getOpeningBracket(closing: string): string {
    return { "]": "[", "}": "{", ")": "(" }[closing] ?? "";
  }

  // LSEG: ForexUtils_75582.validateForAdditionalTenorTypes
  private validateForAdditionalTenorTypes(bracketed: BracketedOverride, data: ForexData, fieldName: string): void {
    let list = bracketed.override;
    if (bracketed.label.trim().toLowerCase() !== ADDITIONAL_TENOR_TYPES_LABEL) {
      this.validate(false, fieldName, "provided wrong additional tenor types label");
    }
    if (list.includes(",") || list.includes(";") || list.includes("|")) list = list.replace(/\s/g, "");
    list.split(bracketed.separators).forEach((name) => {
      const key = name.trim().toLowerCase();
      if (!ADDITIONAL_TENOR_TYPES.has(key)) this.validate(false, fieldName, "provided wrong parameter");
      data.additionalTenorTypes.push(ADDITIONAL_TENOR_TYPES.get(key));
    });
  }

  /** "1M(10;12)": a tenor override when the brackets hold a number, else additional tenor types. */
  // LSEG: ForexUtils_75582.validateValueInBrackets
  private validateValueInBrackets(bracketed: BracketedOverride, data: ForexData, fieldName: string): void {
    if (!/\d+(\.\d+)?/.test(bracketed.override)) {
      this.validateForAdditionalTenorTypes(bracketed, data, fieldName);
      return;
    }
    if (!this.isTenor(bracketed.label.toUpperCase())) this.validate(false, fieldName, "provided wrong tenor for overrides");
    const tenor = bracketed.label.toUpperCase();
    const { bid, ask } = this.getBitAskValues(bracketed.override);
    data.overrides.push({
      ...(tenor ? { tenor } : {}),
      ...(bid ? { bid: +bid } : {}),
      ...(ask ? { ask: +ask } : {}),
    });
  }

  /** "bid:1.1;ask:1.2" or "1.1 1.2" (bid then ask). */
  // LSEG: ForexUtils_75582.validateBitAskValues
  private validateBitAskValues(parts: string[]): { bid?: string; ask?: string } {
    const out: { bid?: string; ask?: string } = {};
    if (parts[0]) {
      const first = parts[0].toLowerCase();
      if (first.includes("bid")) out.bid = parts[0].split(":")[1];
      else if (first.includes("ask")) out.ask = parts[0].split(":")[1];
      else out.bid = parts[0];
    }
    if (parts[1]) {
      const second = parts[1].toLowerCase();
      if (second.includes("bid")) out.bid = parts[1].split(":")[1];
      else if (second.includes("ask")) out.ask = parts[1].split(":")[1];
      else out.ask = parts[1];
    }
    return out;
  }

  /**
   * The bid and ask of an override. LSEG returns `[override]` for an empty
   * one, which only an empty override reaches, after "override is missing".
   */
  // LSEG: ForexUtils_75582.getBitAskValues
  private getBitAskValues(override: string): { bid?: string; ask?: string } {
    let values = override.replace(/\s+/g, " ");
    if (values.includes(",") || values.includes(";") || values.includes("|")) values = values.replace(/\s/g, "");
    return this.validateBitAskValues(values.split(BID_ASK_SEPARATORS));
  }

  // LSEG: ForexUtils_75582.confirmOpeningBracket
  private confirmOpeningBracket(state: ReadState): void {
    if (state.insideBrackets) {
      state.openingBracketCount++;
    } else {
      state.insideBrackets = true;
      state.bracketType = state.char;
      state.openingBracketCount = 1;
    }
    state.currentValue += state.char;
  }

  // LSEG: ForexUtils_75582.confirmClosingBracket
  private confirmClosingBracket(state: ReadState, data: ForexData, isSpot: boolean, fieldName: string): void {
    state.currentValue += state.char;
    if (!state.insideBrackets) return;
    if (state.bracketType !== this.getOpeningBracket(state.char)) {
      this.validate(false, fieldName, "Opening and closing brackets of the overrides do not match");
    }
    state.openingBracketCount--;
    if (state.openingBracketCount === 0) {
      state.insideBrackets = false;
      this.validateForexOverride(state, data, isSpot, fieldName);
      state.currentValue = "";
    }
  }

  /** A space ends a parameter, outside brackets. */
  // LSEG: ForexUtils_75582.confirmValuesSeparator
  private confirmValuesSeparator(state: ReadState, data: ForexData, fieldName: string): void {
    if (state.insideBrackets) {
      state.currentValue += state.char;
    } else if (state.currentValue.trim() !== "") {
      this.validateForexSingleParameter(state.currentValue, data, fieldName);
      state.currentValue = "";
    }
  }
}

// ---------------------------------------------------------------- spot from a range

/** What processSpotCcy found. */
type SpotState = {
  source: string;
  bid: string;
  ask: string;
  numberAlreadyExist: boolean;
  bidNameMatch: boolean;
  askNameMatch: boolean;
};

/** The first rows (or columns) LSEG reads in a two-dimensional spot range. */
// LSEG: SpotCellRangeProcessor_64954 (its maximumColumnValues)
const MAXIMUM_COLUMN_VALUES = [0, 1, 2, 3, 4];

/** A cell "" / null / undefined. */
function isEmptyCell(cell: unknown): boolean {
  return cell === "" || cell === null || cell === undefined;
}

/**
 * A spot override (source, bid, ask) from a cell range: one row or one
 * column of cells, or a two-dimensional layout starting with a "Source"
 * label, a source, or an override ("Spot", "Bid", "Ask" or a number).
 */
// LSEG: SpotCellRangeProcessor_64954.SpotCellRangeProcessor
export class SpotCellRangeProcessor {
  private readonly adFinUtils = new AdFinUtils();
  private validationLabel = "";

  // LSEG: SpotCellRangeProcessor_64954.getSpotDataObject
  private getSpotDataObject(state: SpotState): SpotOverride {
    return {
      ...(state.source ? { source: state.source } : {}),
      ...(state.bid ? { bid: +state.bid } : {}),
      ...(state.ask ? { ask: +state.ask } : {}),
    };
  }

  // LSEG: SpotCellRangeProcessor_64954.processSpotCcy
  processSpotCcy(range: CellRange, fieldName: string): SpotOverride | undefined {
    this.validationLabel = fieldName;
    const state: SpotState = { source: "", bid: "", ask: "", numberAlreadyExist: false, bidNameMatch: false, askNameMatch: false };
    if (range.length === 1) {
      range[0].forEach((cell) => this.processSingleLineCell(cell, state));
      return this.getSpotDataObject(state);
    }
    return range.length > 1 ? this.processSpotOnMultipleRows(range, state) : undefined;
  }

  // LSEG: SpotCellRangeProcessor_64954.processSpotOnMultipleRows
  private processSpotOnMultipleRows(range: CellRange, state: SpotState): SpotOverride | undefined {
    if (range.every((row) => row.length === 1)) {
      range.forEach((row) => this.processSingleLineCell(row[0], state));
      return this.getSpotDataObject(state);
    }
    if (range[0].length < 1) return undefined;
    const first = range[0][0];
    if (this.adFinUtils.compareLabel(first, SOURCE_LABEL)) return this.getSpotStartingWithSourceLabel(range, state);
    if (isFxSource(first?.toString().trim().toUpperCase())) return this.getSpotStartingWIthSourceValue(range, state);
    if (
      this.adFinUtils.compareLabel(first, SPOT_LABEL) ||
      this.adFinUtils.compareLabel(first, "bid") ||
      this.adFinUtils.compareLabel(first, "ask") ||
      isNumber(first) ||
      typeof first === "number"
    ) {
      return this.getSpotStartingWithOverride(range, state);
    }
    this.adFinUtils.validate(false, this.validationLabel, "provided wrong spot argument");
    return undefined;
  }

  /** "Source" first: the source under it (then column 2 is read) or beside it (then row 2 is read). */
  // LSEG: SpotCellRangeProcessor_64954.getSpotStartingWithSourceLabel
  private getSpotStartingWithSourceLabel(range: CellRange, state: SpotState): SpotOverride | undefined {
    if (isFxSource(range[1][0]?.toString().trim().toUpperCase())) {
      state.source = this.getCorrectSource(text(range[1][0]).trim().toUpperCase());
      this.processColumn(range, 1, state);
      return this.getSpotDataObject(state);
    }
    if (isFxSource(range[0][1]?.toString().trim().toUpperCase())) {
      state.source = this.getCorrectSource(text(range[0][1]).trim().toUpperCase());
      range[1].forEach((cell) => this.processSingleLineCell(cell, state));
      return this.getSpotDataObject(state);
    }
    this.adFinUtils.validate(false, this.validationLabel, "provided wrong spot argument");
    return undefined;
  }

  /** A source first, an empty cell under it (column 2 is read) or beside it (row 2 is read). */
  // LSEG: SpotCellRangeProcessor_64954.getSpotStartingWIthSourceValue
  private getSpotStartingWIthSourceValue(range: CellRange, state: SpotState): SpotOverride | undefined {
    state.source = this.getCorrectSource(text(range[0][0]).trim().toUpperCase());
    if (isEmptyCell(range[1][0])) {
      this.processColumn(range, 1, state);
      return this.getSpotDataObject(state);
    }
    if (isEmptyCell(range[0][1])) {
      range[1].forEach((cell) => this.processSingleLineCell(cell, state));
      return this.getSpotDataObject(state);
    }
    this.adFinUtils.validate(false, this.validationLabel, "provided wrong source");
    return undefined;
  }

  /** An override first: a source beside it (column 1 is read) or a bid/ask beside it (row 1 is read, the source under). */
  // LSEG: SpotCellRangeProcessor_64954.getSpotStartingWithOverride
  private getSpotStartingWithOverride(range: CellRange, state: SpotState): SpotOverride | undefined {
    const beside = range[0][1];
    const besideLower = beside?.toString().trim().toLowerCase();
    if (besideLower === SOURCE_LABEL || isFxSource(beside?.toString().trim().toUpperCase())) {
      state.source = this.getCorrectSource(this.validateSourcePosition(beside, range[1][1]));
      this.processColumn(range, 0, state);
      return this.getSpotDataObject(state);
    }
    if (besideLower === "bid" || besideLower === "ask" || isNumber(beside) || typeof beside === "number") {
      const source = isFxSource(range[1][0]) ? range[1][0] : isFxSource(range[1][1]) ? range[1][1] : "";
      state.source = this.getCorrectSource(source as string);
      range[0].forEach((cell) => this.processSingleLineCell(cell, state));
      return this.getSpotDataObject(state);
    }
    this.adFinUtils.validate(false, this.validationLabel, "provided wrong source");
    return undefined;
  }

  /** Column `col` of the first five rows. */
  // LSEG: SpotCellRangeProcessor_64954 (its maximumColumnValues loop)
  private processColumn(range: CellRange, col: number, state: SpotState): void {
    MAXIMUM_COLUMN_VALUES.forEach((r) => this.processSingleLineCell((range[r] ?? [])[col], state));
  }

  // LSEG: SpotCellRangeProcessor_64954.processCellAsText
  private processCellAsText(cell: string, state: SpotState): void {
    const lower = cell.toLowerCase();
    if (isFxSource(cell.trim().toUpperCase()) || cell.trim() === COMPOSITE) state.source = cell.toUpperCase();
    else if (lower === "bid") state.bidNameMatch = true;
    else if (lower === "ask") state.askNameMatch = true;
    else if (isNumber(cell)) this.checkIfTextIsNumber(cell, state);
    else if (lower !== SPOT_LABEL && lower !== SOURCE_LABEL) {
      this.adFinUtils.validate(false, this.validationLabel, "provided wrong spot argument");
    }
  }

  // LSEG: SpotCellRangeProcessor_64954.processCellAsNumber
  private processCellAsNumber(cell: number, state: SpotState): void {
    if (isNumber(cell.toString())) this.checkIfTextIsNumber(cell.toString(), state);
  }

  /** A text cell or a number cell; anything else is refused. Blank cells are skipped. */
  // LSEG: SpotCellRangeProcessor_64954.processSingleLineCell
  private processSingleLineCell(cell: unknown, state: SpotState): void {
    if (!cell) return;
    if (typeof cell === "string") this.processCellAsText(cell, state);
    else if (typeof cell === "number") this.processCellAsNumber(cell, state);
    else this.adFinUtils.validate(false, this.validationLabel, "provided wrong spot argument");
  }

  // LSEG: SpotCellRangeProcessor_64954.validateSourcePosition
  private validateSourcePosition(first: unknown, second: unknown): string {
    return isFxSource(first) ? (first as string) : isFxSource(second) ? (second as string) : "";
  }

  /** COMPOSITE and REFINITIV as LSEG sends them ("Composite", "Refinitiv"). */
  // LSEG: SpotCellRangeProcessor_64954.getCorrectSource
  private getCorrectSource(source: string): string {
    if (!source) return "";
    return source === "REFINITIV" || source === "COMPOSITE" ? (FX_SOURCE_VALUES.get(source) as string) : source;
  }

  /**
   * A number goes to the place a "Bid" / "Ask" label just named, else to bid
   * then ask. (LSEG's processCellAsNumber does the same with the number's text.)
   */
  // LSEG: SpotCellRangeProcessor_64954.checkIfTextIsNumber
  private checkIfTextIsNumber(value: string, state: SpotState): void {
    if (state.bidNameMatch || state.askNameMatch) {
      if (state.bidNameMatch) {
        state.bid = value;
        state.bidNameMatch = false;
      }
      if (state.askNameMatch) {
        state.ask = value;
        state.askNameMatch = false;
      }
    } else if (state.numberAlreadyExist) {
      state.ask = value;
    } else {
      state.bid = value;
      state.numberAlreadyExist = true;
    }
  }
}

// ---------------------------------------------------------------- zero-coupon curve from a range

const ZC = "ZcCurve";
const ZC_CURVE_LABEL = "zccurve";
const CODE_LABEL = "code";
const CURVE_POINTS_LABEL = "curvepoints";
const TENOR_LABEL = "tenor";
const VALUE_LABEL = "value";
const UNIT_LABEL = "unit";
const POINT_LABELS: unknown[] = [TENOR_LABEL, VALUE_LABEL, UNIT_LABEL];
const DIGITS = /(\d+(?:\.\d+)?)/;

/**
 * Curve-point units by lower-case name. A plain object, looked up the way
 * LSEG looks it up (`units[name]`).
 */
// LSEG: ZcCurveCellRangeProcessor_28099 (its allowedCurvePointUnits)
export const CURVE_POINT_UNITS: Record<string, string> = { discountfactor: "DiscountFactor", ratepercent: "RatePercent" };

/** `cell` lower-cased; undefined for null or undefined ([A] a number is read as its text, see `text`). */
function lower(cell: unknown): string | undefined {
  return cell === null || cell === undefined ? undefined : text(cell).toLowerCase();
}

/** The unit `cell` names, or undefined. */
function unitOf(cell: unknown): string | undefined {
  const key = lower(cell);
  return key === undefined ? undefined : CURVE_POINT_UNITS[key];
}

/** Where the curve lies in the range, and what has been read. */
export type ZcCurveState = {
  currencyCode: unknown;
  startingRowIndex: number;
  startingColIndex: number;
  isHorizontal: boolean;
  isVertical: boolean;
  curvePointsLabelsSet: unknown[];
  curvePointsLabelsExist: boolean;
};

/** A cell of the curve being read. */
type ZcCell = { firstCell: boolean; cell: unknown; labelIndex: number; lastIndex: boolean };
/** A curve point being read. */
type ZcPoint = { tenor: unknown; value: number; unit: unknown };

/**
 * The cell at (row, col), undefined past the range's end: LSEG's guarded read
 * `(range[row] ?? [])[col]`. Its few unguarded `range[row][col]` reads only
 * reach rows that exist, so they read the same.
 */
function cellAt(range: CellRange, row: number, col: number): unknown {
  return (range[row] ?? [])[col];
}

/**
 * A zero-coupon curve from a cell range: one cell is the code; otherwise a
 * code then its points (tenor, value, optional unit) in a row or a column,
 * with optional "zcCurve", "code", "curvePoints" and "tenor / value / unit" labels.
 */
// LSEG: ZcCurveCellRangeProcessor_28099.ZcCurveCellRangeProcessor
export class ZcCurveCellRangeProcessor {
  private readonly adFinUtils = new AdFinUtils();
  private readonly advancedCRProcessor = new ZcCurveAdvancedCRProcessor();
  private readonly simpleCRProcessor = new ZcCurveSimpleCRProcessor();

  // LSEG: ZcCurveCellRangeProcessor_28099.processZcCurve
  processZcCurve(range: CellRange): ZcCurve {
    const state: ZcCurveState = {
      currencyCode: "",
      startingRowIndex: 0,
      startingColIndex: 0,
      isHorizontal: false,
      isVertical: false,
      curvePointsLabelsSet: [],
      curvePointsLabelsExist: false,
    };
    state.currencyCode = this.simpleCRProcessor.validateSingleCellForCode(range);
    if (state.currencyCode) return withoutEmptyValues({ code: state.currencyCode, curvePoints: [] });
    const [code, [isHorizontal, isVertical, row, col]] = this.simpleCRProcessor.processSimpleZcCurveScenarios(
      range,
      state.currencyCode,
    );
    state.currencyCode = code;
    if (isHorizontal || isVertical) {
      state.isVertical = isVertical;
      state.isHorizontal = isHorizontal;
      state.startingRowIndex = row;
      state.startingColIndex = col;
    } else {
      this.advancedCRProcessor.processAdvancedZcCurveScenarios(range, state);
    }
    state.curvePointsLabelsExist = this.simpleCRProcessor.validateCurvePointsLabelsSet(state.curvePointsLabelsSet);
    const curve: Required<ZcCurve> = { code: state.currencyCode, curvePoints: [] };
    this.buildZcCurveObject(state, range, curve);
    return withoutEmptyValues(curve);
  }

  // LSEG: ZcCurveCellRangeProcessor_28099.buildZcCurveObject
  private buildZcCurveObject(state: ZcCurveState, range: CellRange, curve: Required<ZcCurve>): void {
    const point: ZcPoint = { tenor: "", value: 0, unit: "" };
    if (state.isHorizontal) this.buildHorizontalZcCurveData(state, range, point, curve);
    else if (state.isVertical) this.buildVerticalZcCurveData(state, range, point, curve);
  }

  /** Row by row from the start, each row up to its first empty cell. */
  // LSEG: ZcCurveCellRangeProcessor_28099.buildHorizontalZcCurveData
  private buildHorizontalZcCurveData(state: ZcCurveState, range: CellRange, point: ZcPoint, curve: Required<ZcCurve>): void {
    for (let r = state.startingRowIndex; r <= range.length; r++) {
      for (let c = state.startingColIndex; c < (range[r]?.length ?? 0); c++) {
        const next = cellAt(range, r, c + 1) ? range[r][c + 1] : "";
        const cell: ZcCell = {
          firstCell: c === state.startingColIndex,
          cell: range[r][c],
          labelIndex: c,
          lastIndex: range[0].length - 1 === c || !next,
        };
        this.processZcCurveCell(cell, point, state, curve, next);
        if (!next) break;
      }
    }
  }

  /** Column by column from the start, each column down to its first empty cell. */
  // LSEG: ZcCurveCellRangeProcessor_28099.buildVerticalZcCurveData
  private buildVerticalZcCurveData(state: ZcCurveState, range: CellRange, point: ZcPoint, curve: Required<ZcCurve>): void {
    for (let c = state.startingColIndex; c <= range[0].length - 1; c++) {
      for (let r = state.startingRowIndex; r < range.length; r++) {
        const next = cellAt(range, r + 1, c) ? cellAt(range, r + 1, c) : "";
        const cell: ZcCell = {
          firstCell: c === state.startingColIndex && r === state.startingRowIndex,
          cell: cellAt(range, r, c),
          labelIndex: r,
          lastIndex: range.length - 1 === r || !next,
        };
        this.processZcCurveCell(cell, point, state, curve, next);
        if (!next) break;
      }
    }
  }

  /** A tenor, a value or a unit; the last cell of a line closes its point. */
  // LSEG: ZcCurveCellRangeProcessor_28099.processZcCurveCell
  private processZcCurveCell(cell: ZcCell, point: ZcPoint, state: ZcCurveState, curve: Required<ZcCurve>, next: unknown): void {
    if (!cell.cell) this.adFinUtils.validate(false, ZC, "tenor in curvePoints argument is missing");
    if (cell.cell === CODE_LABEL || cell.cell === state.currencyCode) return;
    const isTenor = typeof cell.cell === "string" && this.adFinUtils.isTenor(cell.cell);
    const isValue = typeof cell.cell === "number" || DIGITS.test(text(cell.cell));
    const isUnit = typeof cell.cell === "string" && unitOf(cell.cell);
    if (isTenor) this.simpleCRProcessor.validateIfCellIsTenor(cell, point, state, curve, next);
    else if (isValue) this.simpleCRProcessor.validateIfCellIsValue(state, cell, point, next);
    else if (isUnit) this.simpleCRProcessor.validateIfCellIsUnit(state, cell, point);
    if (cell.lastIndex) {
      curve.curvePoints.push(curvePoint(point));
      point.tenor = "";
      point.value = 0;
      point.unit = "";
    }
  }
}

/** A point's tenor, value and unit, each left out when empty. */
// LSEG: ZcCurveCellRangeProcessor_28099.processZcCurveCell / ZcCurveSimpleCRProcessor_11259.validateIfCellIsTenor (their point object)
function curvePoint(point: ZcPoint): Record<string, unknown> {
  return {
    ...(point.tenor ? { tenor: point.tenor } : {}),
    ...(point.value ? { value: point.value } : {}),
    ...(point.unit ? { unit: point.unit } : {}),
  };
}

/** The layouts with the code first: points under it or after it, in a row or a column. */
// LSEG: ZcCurveSimpleCRProcessor_11259.ZcCurveSimpleCRProcessor
class ZcCurveSimpleCRProcessor {
  private readonly adFinUtils = new AdFinUtils();

  /**
   * [the code, [horizontal, vertical, start row, start column]]; all false
   * when the range does not start with a code this reading knows.
   */
  // LSEG: ZcCurveSimpleCRProcessor_11259.processSimpleZcCurveScenarios
  processSimpleZcCurveScenarios(range: CellRange, code: unknown): [unknown, [boolean, boolean, number, number]] {
    const none: [boolean, boolean, number, number] = [false, false, 0, 0];
    if (code || !range[0][0] || lower(range[0][0]) === ZC_CURVE_LABEL) return [code, none];
    const codeLabelled = lower(range[0][0]) === CODE_LABEL;
    const codeCol = codeLabelled ? 1 : 0;
    const codeCell = range?.[0]?.[codeCol];
    const codeKey = lower(codeCell)?.trim();
    if (!codeKey || [ZC_CURVE_LABEL, CODE_LABEL, CURVE_POINTS_LABEL, TENOR_LABEL, VALUE_LABEL, UNIT_LABEL].includes(codeKey)) {
      return [code, none];
    }
    const afterCol = codeCol + (codeLabelled && range[0][2] && lower(range[0][2]) === CURVE_POINTS_LABEL ? 2 : 1);
    const found = this.validateTenors(afterCol, range, 0, codeCol, codeKey);
    let layout: [boolean, boolean, number, number];
    if (found.curvePointsInColumnUnderCode) layout = [false, true, 1, codeCol];
    else if (found.curvePointsInRowUnderCode) layout = [true, false, 1, codeCol];
    else if (found.curvePointsInRowAfterCode) layout = [true, false, 0, afterCol];
    else if (found.curvePointsInColumnAfterCode) layout = [false, true, 0, afterCol];
    else layout = none;
    return [codeCell, layout];
  }

  /** Where the first tenor and its value lie: under the code or after it, down or across. */
  // LSEG: ZcCurveSimpleCRProcessor_11259.validateTenors
  private validateTenors(
    afterCol: number,
    range: CellRange,
    row: number,
    codeCol: number,
    code: string,
  ): {
    curvePointsInColumnUnderCode: boolean;
    curvePointsInRowUnderCode: boolean;
    curvePointsInColumnAfterCode: boolean;
    curvePointsInRowAfterCode: boolean;
  } {
    const tenorUnder = this.isValidTenor(range?.[row + 1]?.[codeCol], code);
    const valueUnderTenorUnder = this.isValidValue(range?.[row + 2]?.[codeCol], code);
    const valueBesideTenorUnder = this.isValidValue(range?.[row + 1]?.[codeCol + 1], code);
    const tenorAfter = this.isValidTenor(range?.[row]?.[afterCol], code);
    const valueUnderTenorAfter = this.isValidValue(range?.[row + 1]?.[afterCol], code);
    const valueBesideTenorAfter = this.isValidValue(range?.[row]?.[afterCol + 1], code);
    const tenor = tenorUnder ? range[row + 1][codeCol] : tenorAfter ? range[row][afterCol] : "";
    const value = this.getFirstValueAfterTenor(
      {
        validValue1: valueUnderTenorUnder,
        validValue2: valueBesideTenorUnder,
        validValue3: valueUnderTenorAfter,
        validValue4: valueBesideTenorAfter,
      },
      range,
      row,
      codeCol,
      afterCol,
    );
    if (tenor && !this.adFinUtils.isTenor(tenor)) this.adFinUtils.validate(false, ZC, "tenor in curvePoints argument is missing");
    if (value) {
      if (!(typeof value === "number" || DIGITS.test(text(value)))) {
        this.adFinUtils.validate(false, ZC, "mandatory value in curvePoints argument is missing");
      }
    } else if (tenor) {
      this.adFinUtils.validate(false, ZC, "mandatory value in curvePoints argument is missing");
    }
    return {
      curvePointsInColumnUnderCode: tenorUnder && valueUnderTenorUnder,
      curvePointsInRowUnderCode: tenorUnder && valueBesideTenorUnder,
      curvePointsInColumnAfterCode: tenorAfter && valueUnderTenorAfter,
      curvePointsInRowAfterCode: tenorAfter && valueBesideTenorAfter,
    };
  }

  // LSEG: ZcCurveSimpleCRProcessor_11259.getFirstValueAfterTenor
  private getFirstValueAfterTenor(
    valid: { validValue1: boolean; validValue2: boolean; validValue3: boolean; validValue4: boolean },
    range: CellRange,
    row: number,
    codeCol: number,
    afterCol: number,
  ): unknown {
    if (valid.validValue1) return range[row + 2][codeCol];
    if (valid.validValue2) return range[row + 1][codeCol + 1];
    if (valid.validValue3) return range[row + 1][afterCol];
    if (valid.validValue4) return range[row][afterCol + 1];
    return "";
  }

  /** A cell that is neither a label nor the code must be a tenor. */
  // LSEG: ZcCurveSimpleCRProcessor_11259.isValidTenor
  private isValidTenor(cell: unknown, code: string): boolean {
    if (!cell || POINT_LABELS.includes(cell) || lower(cell)?.trim() === code) return false;
    if (!this.adFinUtils.isTenor(cell)) this.adFinUtils.validate(false, ZC, "tenor in curvePoints argument is missing");
    return true;
  }

  /** A number (or a text with a digit) that is neither a label, the code nor a tenor. */
  // LSEG: ZcCurveSimpleCRProcessor_11259.isValidValue
  private isValidValue(cell: unknown, code: string): boolean {
    if (!cell) return false;
    if (POINT_LABELS.includes(cell) || (typeof cell === "string" && cell.toLowerCase().trim() === code)) return false;
    return !(this.adFinUtils.isTenor(cell) || (typeof cell !== "number" && !DIGITS.test(text(cell))));
  }

  /** A tenor starts a point (closing the one before, when it has a value); a value must follow it. */
  // LSEG: ZcCurveSimpleCRProcessor_11259.validateIfCellIsTenor
  validateIfCellIsTenor(cell: ZcCell, point: ZcPoint, state: ZcCurveState, curve: Required<ZcCurve>, next: unknown): void {
    const label = state.curvePointsLabelsSet[cell.labelIndex];
    if (state.curvePointsLabelsExist && label && label !== TENOR_LABEL) {
      this.adFinUtils.validate(false, ZC, "tenor in curvePoints doesn't match with corresponding label");
    }
    if (!next) this.adFinUtils.validate(false, ZC, "mandatory value in curvePoints argument is missing");
    if (!DIGITS.test(text(next))) this.adFinUtils.validate(false, ZC, "mandatory value in curvePoints argument is missing");
    if (point.value && !cell.firstCell) {
      curve.curvePoints.push(curvePoint(point));
      point.tenor = cell.cell;
      point.value = 0;
      point.unit = "";
    } else {
      point.tenor = cell.cell;
    }
  }

  /** A value; what follows it must be a tenor or a unit. */
  // LSEG: ZcCurveSimpleCRProcessor_11259.validateIfCellIsValue
  validateIfCellIsValue(state: ZcCurveState, cell: ZcCell, point: ZcPoint, next: unknown): void {
    const label = state.curvePointsLabelsSet[cell.labelIndex];
    if (state.curvePointsLabelsExist && label && label !== VALUE_LABEL) {
      this.adFinUtils.validate(false, ZC, "value in curvePoints doesn't match with corresponding label");
    }
    if (next && !this.adFinUtils.isTenor(next) && !unitOf(next)) {
      this.adFinUtils.validate(
        false,
        ZC,
        "provided optional unit in curvePoints argument is wrong. Should be either DiscountFactor or RatePercent",
      );
    }
    point.value = Number(cell.cell);
  }

  // LSEG: ZcCurveSimpleCRProcessor_11259.validateIfCellIsUnit
  validateIfCellIsUnit(state: ZcCurveState, cell: ZcCell, point: ZcPoint): void {
    const label = state.curvePointsLabelsSet[cell.labelIndex];
    if (state.curvePointsLabelsExist && label && label !== UNIT_LABEL) {
      this.adFinUtils.validate(false, ZC, "unit in curvePoints doesn't match with corresponding label");
    }
    point.unit = unitOf(cell.cell);
  }

  /** The code of a one-cell range ("" otherwise); an empty one is refused. */
  // LSEG: ZcCurveSimpleCRProcessor_11259.validateSingleCellForCode
  validateSingleCellForCode(range: CellRange): unknown {
    if (range.length === 1 && range[0].length === 1) {
      if (!this.adFinUtils.isEmpty(range[0][0])) return range[0][0];
      this.adFinUtils.validate(false, ZC, "code is missing");
    } else if (range.length === 1 && range[0].length === 0) {
      this.adFinUtils.validate(false, ZC, "code is missing");
    }
    return "";
  }

  /** The labels from the first "tenor" on must run tenor, value, then unit or tenor. Always true. */
  // LSEG: ZcCurveSimpleCRProcessor_11259.validateCurvePointsLabelsSet
  validateCurvePointsLabelsSet(labels: unknown[]): boolean {
    for (let i = this.getFirstTenorLabelIndex(labels); i < labels.length; i++) {
      const label = lower(labels[i])?.trim();
      if (!label) continue;
      this.validateLabel(label, lower(labels[i + 1])?.trim());
    }
    return true;
  }

  // LSEG: ZcCurveSimpleCRProcessor_11259.getFirstTenorLabelIndex
  private getFirstTenorLabelIndex(labels: unknown[]): number {
    let i = 0;
    for (; i < labels.length; i++) {
      const label = lower(labels[i])?.trim();
      if (!label) continue;
      if (label === VALUE_LABEL || label === UNIT_LABEL) this.adFinUtils.validate(false, ZC, "tenor label should be provided first");
      if (label === TENOR_LABEL) break;
    }
    return i;
  }

  // LSEG: ZcCurveSimpleCRProcessor_11259.validateLabel
  private validateLabel(label: string, next: string | undefined): void {
    const wrong = () => this.adFinUtils.validate(false, ZC, "provided wrong curvePoints label");
    if (label === TENOR_LABEL) {
      if (!next) this.adFinUtils.validate(false, ZC, "missing value label within curvePoints labels set");
      else if (next !== VALUE_LABEL) wrong();
    } else if (label === VALUE_LABEL) {
      if (next && next !== TENOR_LABEL && next !== UNIT_LABEL) wrong();
    } else if (label === UNIT_LABEL) {
      if (next && next !== TENOR_LABEL) wrong();
    } else {
      wrong();
    }
  }
}

/** The labelled layouts and those with a blank first cell. */
// LSEG: ZcCurveAdvancedCRProcessor_52623.ZcCurveAdvancedCRProcessor
class ZcCurveAdvancedCRProcessor {
  private readonly adFinUtils = new AdFinUtils();

  /** A tenor, a number, a text "<number>&" or a unit (truthy cells only). */
  // LSEG: ZcCurveAdvancedCRProcessor_52623 (its isTenorOrValueOrUnit)
  private isTenorOrValueOrUnit(cell: unknown): boolean {
    if (!cell) return false;
    return (
      this.adFinUtils.isTenor(cell) || typeof cell === "number" || /^(\d+(?:\.\d+)?)&/.test(text(cell)) || !!unitOf(cell)
    );
  }

  /**
   * A first cell that is not blank is skipped when it is "zcCurve"; then the
   * next cell is read. (LSEG's checkFirstCellIfLabel always returns true, so
   * its two other branches never run and are not ported.)
   */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.processAdvancedZcCurveScenarios
  processAdvancedZcCurveScenarios(range: CellRange, state: ZcCurveState): void {
    if (this.adFinUtils.isEmpty(cellAt(range, state.startingRowIndex, state.startingColIndex))) {
      this.checkCurvePointsWithStartingEmptyCell(range, state);
      return;
    }
    this.checkFirstCellIfLabel(range, state);
    this.checkNextCell(range, state);
  }

  // LSEG: ZcCurveAdvancedCRProcessor_52623.checkFirstCellIfLabel
  private checkFirstCellIfLabel(range: CellRange, state: ZcCurveState): boolean {
    if (lower(cellAt(range, state.startingRowIndex, state.startingColIndex)) === ZC_CURVE_LABEL) state.startingColIndex += 1;
    return true;
  }

  // LSEG: ZcCurveAdvancedCRProcessor_52623.checkNextCell
  private checkNextCell(range: CellRange, state: ZcCurveState): void {
    const cell = cellAt(range, state.startingRowIndex, state.startingColIndex);
    if (this.adFinUtils.isEmpty(cell)) {
      this.checkCurvePointsWithStartingEmptyCell(range, state);
    } else if (cell === CODE_LABEL) {
      this.validateNextCellHorizontalData(range, state);
      this.validateNextCellVerticalData(range, state);
    } else {
      this.validateNextCellWhenNotCode(range, state);
    }
  }

  /** "code" then "curvePoints" across: the code and the points are on the rows below. */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.validateNextCellHorizontalData
  private validateNextCellHorizontalData(range: CellRange, state: ZcCurveState): void {
    const beside = cellAt(range, state.startingRowIndex, state.startingColIndex + 1);
    if (!beside) return;
    if (lower(beside) === CURVE_POINTS_LABEL) {
      state.isHorizontal = true;
      state.startingRowIndex += 1;
      this.saveRowCurrencyCode(range, state);
      state.startingColIndex += 1;
      this.saveRowTenor(range, state);
    } else {
      state.currencyCode = beside;
    }
  }

  /** "code" then "curvePoints" down: the code and the points are in the columns after. */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.validateNextCellVerticalData
  private validateNextCellVerticalData(range: CellRange, state: ZcCurveState): void {
    const under = cellAt(range, state.startingRowIndex + 1, state.startingColIndex);
    if (under && lower(under) === CURVE_POINTS_LABEL) {
      state.isVertical = true;
      state.startingColIndex += 1;
      this.saveColumnCurrencyCode(range, state);
      state.startingRowIndex += 1;
      this.saveColumnTenor(range, state);
    }
  }

  /** The cell is the code; the points follow across (or else down), from the next cell when it is a tenor. */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.validateNextCellWhenNotCode
  private validateNextCellWhenNotCode(range: CellRange, state: ZcCurveState): void {
    const row = state.startingRowIndex;
    const col = state.startingColIndex;
    if (this.isTenorOrValueOrUnit(cellAt(range, row, col))) {
      this.adFinUtils.validate(false, ZC, "mandatory code is missing");
      return;
    }
    state.currencyCode = cellAt(range, row, col);
    if (cellAt(range, row, col + 1)) {
      state.isHorizontal = true;
      if (this.adFinUtils.isTenor(cellAt(range, row, col + 1))) state.startingColIndex += 1;
    } else if (cellAt(range, row + 1, col)) {
      state.isVertical = true;
      if (this.adFinUtils.isTenor(cellAt(range, row + 1, col))) state.startingRowIndex += 1;
    }
  }

  /** The code at the start, or else under it; a tenor, value or unit there is refused. */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.saveRowCurrencyCode
  private saveRowCurrencyCode(range: CellRange, state: ZcCurveState): void {
    this.saveCurrencyCode(range, state, [
      [state.startingRowIndex, state.startingColIndex],
      [state.startingRowIndex + 1, state.startingColIndex],
    ]);
  }

  /** The code at the start, or else beside it; a tenor, value or unit there is refused. */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.saveColumnCurrencyCode
  private saveColumnCurrencyCode(range: CellRange, state: ZcCurveState): void {
    this.saveCurrencyCode(range, state, [
      [state.startingRowIndex, state.startingColIndex],
      [state.startingRowIndex, state.startingColIndex + 1],
    ]);
  }

  /** The shared body of saveRowCurrencyCode and saveColumnCurrencyCode: the first place, else the second. */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.saveRowCurrencyCode / saveColumnCurrencyCode
  private saveCurrencyCode(range: CellRange, state: ZcCurveState, [first, second]: [number, number][]): void {
    const at = !this.adFinUtils.isEmpty(cellAt(range, ...first)) ? first : cellAt(range, ...second) ? second : undefined;
    if (!at || this.isTenorOrValueOrUnit(cellAt(range, ...at))) {
      this.adFinUtils.validate(false, ZC, "mandatory code is missing");
      return;
    }
    state.currencyCode = cellAt(range, ...at);
  }

  /** A "tenor" label row is kept as the labels; then the first cell must be a tenor. */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.saveRowTenor
  private saveRowTenor(range: CellRange, state: ZcCurveState): void {
    if (cellAt(range, state.startingRowIndex, state.startingColIndex) === TENOR_LABEL) {
      state.curvePointsLabelsSet = range[state.startingRowIndex];
      state.startingRowIndex += 1;
      const first = cellAt(range, state.startingRowIndex, state.startingColIndex);
      if (first && !this.adFinUtils.isTenor(first)) this.adFinUtils.validate(false, ZC, "tenor in curvePoints argument is missing");
    } else if (!this.adFinUtils.isTenor(cellAt(range, state.startingRowIndex, state.startingColIndex))) {
      this.adFinUtils.validate(false, ZC, "tenor in curvePoints argument is missing");
    }
  }

  /** A "tenor" label column is kept as the labels; then the first cell must be a tenor. */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.saveColumnTenor
  private saveColumnTenor(range: CellRange, state: ZcCurveState): void {
    if (lower(cellAt(range, state.startingRowIndex, state.startingColIndex)) === TENOR_LABEL) {
      for (const row of range) state.curvePointsLabelsSet.push(row[state.startingColIndex]);
      state.startingColIndex += 1;
      const first = cellAt(range, state.startingRowIndex, state.startingColIndex);
      if (first && !this.adFinUtils.isTenor(first)) this.adFinUtils.validate(false, ZC, "tenor in curvePoints argument is missing");
    } else if (!this.adFinUtils.isTenor(cellAt(range, state.startingRowIndex, state.startingColIndex))) {
      this.adFinUtils.validate(false, ZC, "tenor in curvePoints argument is missing");
    }
  }

  /** A blank first cell: "tenor" under it (labels down) or beside it (labels across). */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.checkCurvePointsWithStartingEmptyCell
  private checkCurvePointsWithStartingEmptyCell(range: CellRange, state: ZcCurveState): void {
    const row = state.startingRowIndex;
    const col = state.startingColIndex;
    if (lower(cellAt(range, row + 1, col)) === TENOR_LABEL) {
      state.isVertical = true;
      for (const r of range) state.curvePointsLabelsSet.push(r[col]);
      this.rowCodeCellExist(range, state);
      this.tenorCellExist(range, state);
    } else if (lower(cellAt(range, row, col + 1)) === TENOR_LABEL) {
      state.isHorizontal = true;
      state.curvePointsLabelsSet = range[row];
      this.columnCodeCellExist(range, state);
      this.tenorCellExist(range, state);
    } else {
      this.adFinUtils.validate(false, ZC, "tenor labels are not provided");
    }
  }

  /** The code beside the blank first cell. */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.rowCodeCellExist
  private rowCodeCellExist(range: CellRange, state: ZcCurveState): void {
    const cell = cellAt(range, state.startingRowIndex, state.startingColIndex + 1);
    if (!this.adFinUtils.isEmpty(cell) && !this.isTenorOrValueOrUnit(cell)) state.currencyCode = cell;
    else this.adFinUtils.validate(false, ZC, "mandatory code is missing");
  }

  /**
   * The code under the blank first cell. LSEG's emptiness test here reads
   * `!isEmpty(row)[col]`, which is always true, so only the
   * tenor/value/unit test is ported.
   */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.columnCodeCellExist
  private columnCodeCellExist(range: CellRange, state: ZcCurveState): void {
    const cell = cellAt(range, state.startingRowIndex + 1, state.startingColIndex);
    if (!this.isTenorOrValueOrUnit(cell)) state.currencyCode = cell;
    else this.adFinUtils.validate(false, ZC, "mandatory code is missing");
  }

  /** The points start diagonally from the blank first cell, at a tenor. */
  // LSEG: ZcCurveAdvancedCRProcessor_52623.tenorCellExist
  private tenorCellExist(range: CellRange, state: ZcCurveState): void {
    const cell = cellAt(range, state.startingRowIndex + 1, state.startingColIndex + 1);
    if (!cell) return;
    if (!this.adFinUtils.isTenor(cell)) {
      this.adFinUtils.validate(false, ZC, "tenor in curvePoints argument is missing");
      return;
    }
    state.startingRowIndex += 1;
    state.startingColIndex += 1;
  }
}
